import { useQuery } from "@apollo/client";
import { useCallback, useMemo } from "react";
import { useOutletContext } from "react-router-dom";

import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import DocumentationButton from "components/DocumentationButton";
import { EmptystateServicenowColored } from "components/icons/generated";
import TierInfo from "components/TierInfo";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import EmptyState from "ds/components/EmptyState";
import useAnalytics from "hooks/useAnalytics";
import useCanCreateIntegrations from "shared/Integrations/access/useCanCreateIntegrations";
import { AuthorizationScheme, BillingTierFeature } from "types/generated";
import { getDocsUrl } from "utils/getDocsUrl";

import { useLayoutActions } from "../../hooks/useLayoutActions";
import { useLayoutHeading } from "../../hooks/useLayoutHeading";
import { IntegrationsOutletContext } from "../../types";
import { GET_ACCOUNT_EXTERNAL_INTEGRATIONS } from "../ServiceNow/gql";
import { GetAccountExternalIntegrationsGql } from "../ServiceNow/types";
import { showIntegrationDrawerForm } from "./components/CreateIntegrationForm";
import BackstageIntegrationTable from "./components/Table";

const billingTierFeature = BillingTierFeature.BackstageIntegration;

const BackstageIntegrationsList = () => {
  const canCreateIntegrations = useCanCreateIntegrations(billingTierFeature);
  const trackAnalytics = useAnalytics();
  const { integration } = useOutletContext<IntegrationsOutletContext>();

  const { loading, data } = useQuery<GetAccountExternalIntegrationsGql>(
    GET_ACCOUNT_EXTERNAL_INTEGRATIONS
  );
  const integrations = useMemo(() => {
    const items = data?.externalIntegrations || [];
    const backstageIntegrations = items.filter((integration) => integration.type === "BACKSTAGE");

    return backstageIntegrations;
  }, [data?.externalIntegrations]);

  const isLoginPolicyActive = data?.authorizationScheme === AuthorizationScheme.LoginPolicies;

  const docsUrl = useMemo(() => getDocsUrl("/integrations/external-integrations/backstage"), []);

  const onCreate = useCallback(
    (location: string) => () => {
      trackAnalytics("Create integration clicked", { buttonLocation: location });
      showIntegrationDrawerForm({
        trackAnalytics,
        isLoginPolicyActive,
      });
    },
    [trackAnalytics, isLoginPolicyActive]
  );

  useBreadcrumbs([
    {
      title: "Integrations",
      link: "/integrations",
    },
    {
      title: "Backstage",
    },
  ]);

  useLayoutHeading(integration.name);

  useLayoutActions(
    canCreateIntegrations ? (
      <Button variant="primary" onPress={onCreate("Header")}>
        Set up integration
      </Button>
    ) : null
  );

  return (
    <>
      <TierInfo
        type="callout"
        variant="danger"
        title={`Upgrade plan to use Backstage integrations`}
        feature={billingTierFeature}
      >
        Backstage integrations are not available in your current plan. Upgrade to{" "}
        <strong>Enterprise</strong> plan to use the feature.
      </TierInfo>
      {(!!integrations.length || loading) && (
        <BackstageIntegrationTable integrations={integrations} loadingContent={loading} />
      )}
      {integrations.length === 0 && !loading && (
        <Box align="center" justify="center" grow="1" fullWidth>
          <EmptyState
            padding="large"
            icon={EmptystateServicenowColored}
            title="Integrate with Backstage"
          >
            <Box gap="medium">
              <DocumentationButton to={docsUrl} label="Documentation" />
              {canCreateIntegrations && (
                <Button variant="primary" onPress={onCreate("Empty state")}>
                  Create integration
                </Button>
              )}
            </Box>
          </EmptyState>
        </Box>
      )}
    </>
  );
};

export default BackstageIntegrationsList;
